import React, { useEffect, useState, useRef } from "react"
import {
  CheckboxGroupInput,
  FileUploadInput,
  FormStatus,
  HiddenFields,
  HiddenField,
  NumberInput,
  RangeInput,
  SelectionInput,
  TextArea,
  TextInput,
  SubmitButton,
  ResetButton,
  SelectionMultiInput,
  ImageChoiceInput,
  RichTextInput,
  RadioGroupInput,
  FormStep,
  StepProgress,
} from "./form-elements"
import { shouldDisplayField, evaluateCondition } from "./dependencies"
import Spinner from "../../booking/components/spinner"

export default function EpiForms({
  allowAnonymousSubmission,
  allowExposingDataFeeds,
  allowMultipleSubmission,
  allowToStoreSubmissionData,
  confirmationMessage,
  createdDate,
  description,
  formElements,
  finalizedSubmissionRetentionPeriod,
  formGuid,
  lastSubmissionDate,
  metadataAttribute,
  modifiedDate,
  name,
  partialSubmissionRetentionPeriod,
  redirectToPage,
  redirectUrl,
  showNavigationBar,
  showSummarizedData,
  submitSuccessMessage,
  title,
  authSrc,
  currentPageLink,
  langauge,
  formSubmissionId,
  validationCssClass,
  statusDisplay,
  message,
  formToken,
  backButtonLabel,
  urlValidatorErrorMessage,
  fileUploadPlaceholder,
  fileUploadHelperText,
  fileUploadTooManyFilesError,
  groupingTopPadding,
  groupingBottomPadding,
  recaptchaField,
  reCaptchaSiteKey,
  previousStepLabel,
  nextStepLabel,
  moreInfoLabel,
  closeModalLabel,
}) {
  const [formValues, setFormValues] = useState({})
  const [errors, setErrors] = useState({})
  const [fieldVisibility, setFieldVisibility] = useState({})
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [ajaxSpinnerActive, setAjaxSpinnerActive] = useState(false)
  const [ajaxSpinnerTransparent, setAjaxSpinnerTransparent] = useState(false)
  const [formSteps, setFormSteps] = useState([])
  const [formStepsCount, setFormStepsCount] = useState(0)
  const [activeStep, setActiveStep] = useState(0)
  const stepProgressRef = useRef(null)

  const handleFieldChange = (name, value) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }))
  }

  const setFieldError = (name, error) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }))
  }

  const handleReset = () => {
    setFormValues({})
    setErrors({})
  }

  const isFieldRequired = (validators) => {
    return validators?.some((v) => v.type === "EPiServer.Forms.Implementation.Validation.RequiredValidator") ?? false
  }

  useEffect(() => {
    setFormSteps(getFormSteps(formElements))
  }, [fieldVisibility])

  useEffect(() => {
    const newVisibility = {}
    let visibleStepsCount = 0

    formElements.forEach((element) => {
      if (element.__type__ === "FormGrouping" && element?.groupedFormElements && element.groupedFormElements.length > 0) {
        element.groupedFormElements.forEach((subElement) => {
          newVisibility[subElement.name] = shouldDisplayField(subElement, formValues)
        })
      } else if (element.__type__ === "FormStepBlock") {
        let isVisible = true
        if (element.dependField && element.dependCondition !== 0 && element.dependValue !== null) {
          const fieldValue = formValues[element.dependField]
          const conditionMet = evaluateCondition(element.dependCondition, fieldValue, element.dependValue)

          isVisible = conditionMet ?? false
        }
        newVisibility[element.name] = isVisible
        if (isVisible) {
          visibleStepsCount++
        }
      } else {
        // All other element types
        newVisibility[element.name] = shouldDisplayField(element, formValues)
      }
    })

    setFieldVisibility(newVisibility)
    setFormStepsCount(visibleStepsCount)
  }, [formValues, formElements])

  const validateForm = (formElems) => {
    let firstInvalidField = null
    const newErrors = {}

    formElems.forEach((element) => {
      if (element.__type__ === "FormGrouping" && element?.groupedFormElements?.length > 0) {
        element?.groupedFormElements.forEach((subElement) => {
          if (fieldVisibility[subElement.name]) {
            if (subElement.validators) {
              const required = isFieldRequired(subElement.validators)
              const error = validateField(formValues[subElement.name], subElement.validators, required)
              if (error) {
                newErrors[subElement.name] = error
                setFieldError(subElement.name, error)
                if (!firstInvalidField) {
                  firstInvalidField = document.querySelector(`[name="${subElement.name}"]`)
                }
              }
            } else {
              setFieldError(subElement.name, null)
            }
          }
        })
      } else {
        if (fieldVisibility[element.name]) {
          if (element.validators) {
            const required = isFieldRequired(element.validators)
            const error = validateField(formValues[element.name], element.validators, required)
            if (error) {
              newErrors[element.name] = error
              setFieldError(element.name, error)
              if (!firstInvalidField) {
                firstInvalidField = document.querySelector(`[name="${element.name}"]`)
              }
            }
          } else {
            setFieldError(element.name, null)
          }
        }
      }
    })

    setErrors(newErrors)

    return firstInvalidField
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    let firstInvalidField = validateForm(formElements)

    if (firstInvalidField) {
      scrollToElement(firstInvalidField)
    } else {
      submitFormHandler(e) // No errors, submit the form
    }
  }

  const submitFormHandler = async (e) => {
    e.preventDefault()

    setAjaxSpinnerActive(true)
    setAjaxSpinnerTransparent(false)

    // Create the FormData and append metadata
    const formData = new FormData()
    formData.append("__RequestVerificationToken", formToken)
    formData.append("__FormGuid", formGuid)
    formData.append("__FormHostedPage", currentPageLink)
    formData.append("__FormLanguage", langauge)
    formData.append("__FormSubmissionId", formSubmissionId)

    // Append all form values (only if visible per your logic)
    Object.keys(formValues).forEach((key) => {
      if (!fieldVisibility[key] || !isElementInAnyStep(formSteps, key)) return // only send value if the field is visible or part of a step
      let value = formValues[key]
      if (value instanceof FileList) {
        for (let i = 0; i < value.length; i++) {
          formData.append(key, value[i])
        }
      } else {
        let element = formElements.find((el) => el.name === key)
        if (!element) {
          for (let i = 0; i < formElements.length; i++) {
            element = formElements[i].groupedFormElements?.find((el) => el.name === key)
            if (element) break
          }
        }
        const hasDateValidator = element?.validators?.some((v) =>
          [
            "EPiServer.Forms.Implementation.Validation.DateDDMMYYYYValidator",
            "EPiServer.Forms.Implementation.Validation.DateMMDDYYYYValidator",
            "EPiServer.Forms.Implementation.Validation.DateYYYYMMDDValidator",
          ].includes(v.type),
        )
        if (hasDateValidator && typeof value === "string") {
          value = value.replace(/\s+/g, "")
        }
        formData.append(key, value)
      }
    })

    const submitButton = e.nativeEvent.submitter
    const submitButtonId = submitButton ? submitButton.id : null

    // This inner function will handle the actual POST for a single step.
    const postForm = async () => {
      if (recaptchaField && reCaptchaSiteKey) {
        return new Promise((resolve, reject) => {
          window.grecaptcha.ready(() => {
            window.grecaptcha
              .execute(reCaptchaSiteKey, { action: "submit" })
              .then(async (token) => {
                formData.append(recaptchaField, token)
                try {
                  const response = await fetch(window.location.href, {
                    method: "POST",
                    body: formData,
                    headers: {
                      RequestVerificationToken: formToken,
                    },
                  })
                  if (response.ok) {
                    resolve()
                  } else {
                    console.error("Form submission failed", response.status)
                    reject(new Error("Form submission failed"))
                  }
                } catch (error) {
                  reject(error)
                }
              })
              .catch(reject)
          })
        })
      } else {
        const response = await fetch(window.location.href, {
          method: "POST",
          body: formData,
          headers: {
            RequestVerificationToken: formToken,
          },
        })
        if (!response.ok) {
          console.error("Form submission failed", response.status)
          throw new Error("Form submission failed")
        }
      }
    }

    try {
      if (formSteps != null && formSteps.length > 0) {
        // Submit once for each step (sequentially)
        for (const step of formSteps) {
          // Set the current step index for this submission
          formData.set("__FormCurrentStepIndex", step.stepIndex)
          if (step.stepIndex === formSteps.length - 1 && submitButtonId) {
            // Append the submit button information on last step submit if available
            formData.append(submitButton.name, submitButtonId)
          }
          await postForm()
        }
      } else {
        // If no steps, set to 0 and submit once.
        formData.set("__FormCurrentStepIndex", 0)
        // Append the submit button information if available
        if (submitButtonId) {
          formData.append(submitButton.name, submitButtonId)
        }
        await postForm()
      }
    } catch (error) {
      console.error("Error submitting form", error)
    } finally {
      setAjaxSpinnerActive(false)
      setAjaxSpinnerTransparent(false)
      setFormSubmitted(true)
      const acceptPage = document.querySelector(".accept-page")
      scrollToElement(acceptPage)
    }
  }

  const scrollToElement = (element) => {
    const headerElement = document.querySelector(".site-header.js--site-header")
    const offset = headerElement ? headerElement.offsetHeight + 40 : 0

    const elementPosition = element.getBoundingClientRect().top + window.scrollY - offset

    window.scrollTo({
      top: elementPosition,
      behavior: "smooth",
    })
  }

  const handleBack = () => {
    setFormValues({})
    setErrors({})
    setFormSubmitted(false)
    setActiveStep(0)
  }

  const renderFormElement = (element, key) => {
    if (element.__type__ !== "FormGrouping" && !fieldVisibility[element.name]) {
      return null // Hide element if visibility condition is not met
    }

    const required = isFieldRequired(element.validators)
    const props = {
      ...element,
      closeModalLabel: closeModalLabel,
      moreInfoLabel: moreInfoLabel,
      required: required,
      validateField: (value) => validateField(value, element.validators, required),
      globalValue: formValues[element.name],
      setGlobalValue: (value) => handleFieldChange(element.name, value),
      setError: (error) => setFieldError(element.name, error),
      error: errors[element.name],
    }

    switch (element.__type__) {
      case "TextboxElementBlock":
      case "UrlElementBlock":
      case "TextWithIdBlock":
        return <TextInput key={key} {...props} />
      case "TextareaElementBlock":
        return <TextArea key={key} {...props} />
      case "NumberElementBlock":
        return <NumberInput key={key} {...props} />
      case "RangeElementBlock":
        return <RangeInput key={key} {...props} />
      case "SelectionElementBlock":
        return element.allowMultiSelect ? <SelectionMultiInput key={key} {...props} /> : <SelectionInput key={key} {...props} />
      case "ChoiceElementBlock":
        return element.allowMultiSelect ? <CheckboxGroupInput key={key} {...props} /> : <RadioGroupInput key={key} {...props} />
      case "SignupChoiceBlock":
        return <CheckboxGroupInput key={key} {...props} isPermissionInput={true} />
      case "ImageChoiceElementBlock":
        return <ImageChoiceInput key={key} {...props} />
      case "FileUploadElementBlock":
        return (
          <FileUploadInput
            key={key}
            {...props}
            fileUploadPlaceholder={fileUploadPlaceholder}
            fileUploadHelperText={fileUploadHelperText}
            fileUploadTooManyFilesError={fileUploadTooManyFilesError}
          />
        )
      case "HiddenPredefinedValue":
        return <HiddenField key={key} {...props} />
      case "HiddenVisitorProfiling":
        return <HiddenField key={key} {...props} dataProfiling={true} />
      case "SubmitButtonElementBlock":
        return (
          <SubmitButton
            key={key}
            {...props}
            formStepsCount={formStepsCount}
            setActiveStep={setActiveStep}
            activeStep={activeStep}
            previousStepLabel={previousStepLabel}
            goBack={goBack}
          />
        )
      case "ResetButtonElementBlock":
        return <ResetButton key={key} {...props} formGuid={formGuid} />
      case "ParagraphTextElementBlock":
        return <RichTextInput key={key} {...props} />
      case "RecaptchaElement":
        return <input name={props.name} id={props.name} type="hidden" className="form-input" data-f-datainput="" value="" />
      case "FormGrouping":
        return element?.groupedFormElements?.length > 0 ? (
          <div className={`form-group ${groupingTopPadding} ${groupingBottomPadding}`}>
            {element.groupedFormElements.map((el2, i2) => renderFormElement(el2, el2.name))}
          </div>
        ) : null
      default:
        return null
    }
  }

  const renderFormSteps = (steps) => {
    const currentStep = steps[activeStep]
    const stepElements = currentStep.formStepElements
    return (
      <FormStep
        formElements={stepElements}
        formStepsCount={formStepsCount}
        activeStep={activeStep}
        previousStepLabel={previousStepLabel}
        nextStepLabel={nextStepLabel}
        renderFormElement={renderFormElement}
        goBack={goBack}
        validateStep={() => validateStep(stepElements)}
      />
    )
  }

  const validateField = (value, validators, required) => {
    const checkRegex = (pattern, errorMessage, defaultMsg) => {
      const regex = new RegExp(pattern)
      if (!regex.test(value)) return errorMessage || defaultMsg
    }

    if (!required && !value) return ""

    for (const validator of validators) {
      const { type, errorMessage, regexPattern } = validator

      switch (type) {
        case "EPiServer.Forms.Implementation.Validation.RequiredValidator":
          if (
            (Array.isArray(value) && !value.some((item) => item && item.trim())) ||
            (value instanceof FileList && value.length === 0) ||
            (!Array.isArray(value) && !(value instanceof FileList) && (!value || !value.trim()))
          ) {
            return errorMessage || "Dette felt er obligatorisk."
          }
          break

        case "EPiServer.Forms.Implementation.Validation.EmailValidator":
          if (regexPattern) return checkRegex(regexPattern, errorMessage, "Angiv en gyldig e-mailadresse.")
          break

        case "EPiServer.Forms.Implementation.Validation.RegularExpressionValidator":
          if (regexPattern) return checkRegex(regexPattern, errorMessage, "Dette felt skal have formatet '{pattern}'")
          break

        case "EPiServer.Forms.Implementation.Validation.DateDDMMYYYYValidator":
          if (regexPattern) return checkRegex(regexPattern, errorMessage, "Angiv en dato i formatet DD/MM/YYYY.")
          break

        case "EPiServer.Forms.Implementation.Validation.DateMMDDYYYYValidator":
          if (regexPattern) return checkRegex(regexPattern, errorMessage, "Angiv en dato i formatet MM/DD/YYYY.")
          break

        case "EPiServer.Forms.Implementation.Validation.DateYYYYMMDDValidator":
          if (regexPattern) return checkRegex(regexPattern, errorMessage, "Angiv en dato i formatet YYYY-MM-DD.")
          break

        case "EPiServer.Forms.Implementation.Validation.IntegerValidator":
          if (!Number.isInteger(Number(value))) return errorMessage || "Angiv et gyldigt heltal."
          break

        case "EPiServer.Forms.Implementation.Validation.PositiveIntegerValidator":
          if (!Number.isInteger(Number(value)) || Number(value) <= 0) return errorMessage || "Angiv et positivt heltal."
          break

        case "EPiServer.Forms.Implementation.Validation.UrlValidator":
          if (regexPattern) return checkRegex(regexPattern, errorMessage ?? urlValidatorErrorMessage, "Angiv en URL") // URL validator doesnt have a built in error message from Episerver
          break

        default:
          break
      }
    }
    return "" // No error
  }

  const getFormSteps = (formElements) => {
    if (!fieldVisibility || Object.keys(fieldVisibility).length === 0) {
      return null
    }

    const hasStepBlock = formElements.some((element) => element.__type__ === "FormStepBlock")
    if (!hasStepBlock) {
      return null
    }

    const steps = []
    let currentStep = null
    let stepIndexCounter = 0

    for (const element of formElements) {
      if (element.__type__ === "FormStepBlock") {
        if (currentStep && fieldVisibility[currentStep.name]) {
          steps.push(currentStep)
        }
        const currentIndex = stepIndexCounter
        stepIndexCounter++

        if (fieldVisibility[element.name]) {
          currentStep = { ...element, formStepElements: [], stepIndex: currentIndex }
        } else {
          currentStep = null
        }
      } else if (currentStep) {
        currentStep.formStepElements.push(element)
      }
    }

    if (currentStep && fieldVisibility[currentStep.name]) {
      steps.push(currentStep)
    }

    return steps
  }

  const isElementInAnyStep = (formSteps, elementName) => {
    if (!formSteps || formSteps.length === 0) {
      return true
    }

    return formSteps.some((step) => {
      if (!Array.isArray(step.formStepElements)) {
        return false
      }

      return step.formStepElements.some((element) => {
        if (element.name === elementName) {
          return true
        }

        if (Array.isArray(element.groupedFormElements)) {
          return element.groupedFormElements.some((groupedEl) => groupedEl.name === elementName)
        }

        return false
      })
    })
  }

  const validateStep = (formElements) => {
    let firstInvalidField = validateForm(formElements)

    if (firstInvalidField) {
      scrollToElement(firstInvalidField)
    } else {
      setActiveStep(activeStep + 1) // No errors, go to next step
      if (stepProgressRef.current) scrollToElement(stepProgressRef.current)
    }
  }

  const goBack = () => {
    setActiveStep(activeStep - 1)
    if (stepProgressRef.current) scrollToElement(stepProgressRef.current)
  }

  if (formSubmitted) {
    return (
      <section className="deck deck--white deck--extra-padding-top deck--extra-padding-bottom">
        <div className="epi-forms">
          <div className="w--narrow accept-page">
            <div className="checkmark">
              <svg className={`svg-icon svg-icon--checkmark3`}>
                <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="/dist/svg/symbols.svg#svg-checkmark3">
                  &nbsp;
                </use>
              </svg>
            </div>
            <div className="form-info">
              <h2 className="form-title">{confirmationMessage}</h2>
              <h4 className="form-description" dangerouslySetInnerHTML={{ __html: submitSuccessMessage }}></h4>
            </div>
            <button className="button button--medium button--general" onClick={handleBack}>
              {backButtonLabel || "Tilbage"}
            </button>
            {/* {redirectUrl && (
            <a href={redirectUrl}>
            <button className="button button--medium button--general">Link til side</button>
            </a>
            )} */}
          </div>
        </div>
      </section>
    )
  }

  return (
    <>
      <Spinner isActive={ajaxSpinnerActive} isTransparent={ajaxSpinnerTransparent} />
      <section className="deck deck--white deck--extra-padding-top deck--extra-padding-bottom">
        <form
          className={`epi-forms`}
          method="post"
          noValidate="novalidate"
          data-f-metadata={metadataAttribute}
          encType="multipart/form-data"
          data-f-type="form"
          id={formGuid}
          onSubmit={handleSubmit}
          onReset={handleReset}
        >
          {/* anti forgery token */}
          <input name="__RequestVerificationToken" type="hidden" value={formToken}></input>
          <HiddenFields formGuid={formGuid} currentPageLink={currentPageLink} langauge={langauge} formSubmissionId={formSubmissionId} />
          <FormStatus statusDisplay={statusDisplay} message={message} />
          <section className="deck deck--white">
            <div className="w--narrow">
              <div className="form">
                {formElements?.length > 0 &&
                  (formSteps?.length > 0 ? (
                    <>
                      <StepProgress ref={stepProgressRef} activeStep={activeStep} formStepsCount={formStepsCount} />
                      {renderFormSteps(formSteps)}
                    </>
                  ) : (
                    formElements.map((element, index) => renderFormElement(element, index))
                  ))}
              </div>
            </div>
          </section>
        </form>
      </section>
    </>
  )
}
